<template>
  <!-- Root container for all nav + dialogs -->
  <div
    class="nav-container"
    :class="{ 'nav-container--no-offset': removeNavOffset }"
  >
    <!-- One-time banner if browser is outdated -->
    <BrowserUpdateWarning />

    <!-- Church admin: select reporting locale (EN / PT) when unset -->
    <ChurchLocaleBanner />

    <!-- Top-of-page notification banner (fetches active/all notifications via emitted events) -->
    <DisplayNotifications
      @getActiveNotifications="checkNotifications"
      @getAllNotifications="checkAllNotifications"
      :notificationbackdoor="notificationbackdoor"
      :isNotAdmin="isNotAdmin"
      :user="user"
    />

    <!-- =============================== -->
    <!-- ADMIN / AUTHORIZED NAV WRAPPER -->
    <!-- Renders for verified users who are NOT in the non-admin flow and have specific roles -->
    <!-- =============================== -->
    <nav
      v-if="
        !isNotAdmin &&
        user &&
        user.emailVerified &&
        checkAuthorization(['superAdmin', 'admin', 'leader'])
      "
    >
      <!-- Top app bar (sticky) -->
      <v-app-bar
        height="64"
        :scroll-toolbar-off-screen="smAndDown"
        :scroll-threshold="50"
        :style="{ background: $vuetify.theme.themes[theme].background }"
        class="scrollable-app-bar px-4"
        :class="[
          {
            'top-38': hasNotifications.length,
          } /* Push down when notification banner visible */,
          {
            'shifted-left': notificationDrawer && !smAndDown,
          } /* Shift if right drawer open on desktop */,
        ]"
      >
        <!-- Mobile hamburger toggles main left drawer -->
        <span v-if="smAndDown">
          <v-app-bar-nav-icon @click.stop="drawer = !drawer" />
        </span>

        <!-- Brand/Logo (click -> Home) -->
        <router-link :to="localePath('/')" exact>
          <LogoIcon :color="logoColor" />
        </router-link>

        <!-- Inline toolbar items for admins (left side of top bar) -->
        <v-toolbar-items class="ml-10" v-if="checkAuthorization(['admin'])">
          <!-- Admin Notifications shortcut list (guarded by features flag or tester role) -->
          <div
            class="jrny-orient-btn-container d-flex"
            v-if="
              checkAuthorization(['superAdmin', 'admin']) &&
              (checkAuthorization(['superAdmin', 'tester']) ||
                (getFeatures && getFeatures.includes('notifications')))
            "
          >
            <v-list-item
              icon
              v-for="item in notifications"
              :key="item.icon"
              @click="sendComponent(item)"
              class="px-3"
            >
              <span class="font-weight-bold">{{
                $t(item.titleKey || item.title)
              }}</span>
            </v-list-item>
          </div>

          <!-- Terms management shortcuts (admin only) -->
          <div class="d-flex" v-if="checkAuthorization(['admin'])">
            <v-list-item
              icon
              v-for="item in terms"
              :key="item.icon"
              @click="sendComponent(item)"
              class="px-3"
            >
              <span class="font-weight-bold">{{
                $t(item.titleKey || item.title)
              }}</span>
            </v-list-item>
          </div>
        </v-toolbar-items>

        <!-- Spacer pushes next items to the right -->
        <v-spacer></v-spacer>

        <!-- SuperAdmin church selector (autocomplete) -->
        <v-tooltip location="bottom">
          <!-- Tooltip activator wraps the control to show label on hover -->
          <template #activator="{ props }">
            <span v-bind="props">
              <v-autocomplete
                v-if="showChurchSelect && checkAuthorization(['superAdmin'])"
                @update:modelValue="setChurch"
                v-model="churchid"
                :items="validChurches"
                item-title="displayTitle"
                item-value="cid"
                :return-object="false"
                :custom-filter="customChurchFilter"
                v-model:search-input="churchsearch"
                variant="underlined"
                hide-selected
                hide-details
                label=""
                :placeholder="$t('navbar.autocomplete.placeholder')"
                :no-data-text="$t('navbar.autocomplete.noData')"
                clearable
                :color="$vuetify.theme.dark ? '#CECECE' : '#231815'"
                :menu-props="{
                  contentClass: 'mt-3 church-select-dropdown',
                  maxWidth: '705px',
                  maxHeight: '304px',
                }"
                :virtual-scroll="false"
              >
                <!-- Dropdown row template: shows admin/email if valid entry -->
                <template #item="{ item, props }">
                  <v-list-item v-bind="props">
                    <v-list-item-subtitle v-if="item?.raw?.churchadmin">
                      {{ item.raw.churchadmin }},
                      {{ item.raw.churchadminemail }}
                    </v-list-item-subtitle>
                    <v-list-item-title v-else class="text-caption text-red">{{
                      $t('navbar.autocomplete.invalidEntry')
                    }}</v-list-item-title>
                  </v-list-item>
                </template>

                <!-- Selected chip text (shows computed title) -->
                <template #selection="{ item }">
                  <span>{{ getChurchTitle(item.raw.cid) }}</span>
                </template>
              </v-autocomplete>
            </span>
          </template>
          <span>{{ $t('navbar.tooltips.selectChurch') }}</span>
        </v-tooltip>

        <!-- Right side quick-menu group -->
        <v-toolbar-items class="top-right-menu ml-5">
          <!-- Language selector (uses enabledLangs from Firestore) -->
          <v-menu location="bottom" offset="6">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                variant="text"
                class="px-2"
                density="comfortable"
              >
                <v-icon class="mr-1" size="18">mdi-translate</v-icon>
                <span class="text-button">{{ siteLang.toUpperCase() }}</span>
                <v-icon size="16" class="ml-1">mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list density="compact" class="tj-lang-menu">
              <v-list-item
                v-for="l in enabledLangs"
                :key="l.code"
                @click="onChangeLanguage(l.code)"
              >
                <v-list-item-title
                  class="d-flex align-center justify-space-between"
                >
                  <span>{{ l.label }} ({{ l.code.toUpperCase() }})</span>
                  <v-icon
                    v-if="activeLocale === l.code.toLowerCase()"
                    size="16"
                    color="#A0C548"
                    >mdi-check</v-icon
                  >
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Home icon (routes via sendComponent) -->
          <v-list-item
            class="home-icon pr-3"
            icon
            v-for="item in home"
            :key="item.icon"
            @click="sendComponent(item)"
          >
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-icon
                    :style="{
                      color: $vuetify.theme.dark
                        ? 'var(--v-theme-on-surface)'
                        : 'var(--v-theme-on-background)',
                    }"
                    :icon="'mdi-home'"
                  />
                </span>
              </template>
              <span>{{ $t(item.titleKey || item.title) }}</span>
            </v-tooltip>
          </v-list-item>

          <!-- Notification bell (with unread badge) -->
          <v-btn
            icon
            v-if="
              checkAuthorization(['superAdmin', 'admin', 'leader']) &&
              (checkAuthorization(['superAdmin', 'tester']) ||
                (getFeatures && getFeatures.includes('notifications')))
            "
            @click="showNotifications"
          >
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <!-- Show badge if unreadCount > 0 -->
                <template v-if="shouldShowNotificationBadge">
                  <v-badge
                    :content="unreadCount"
                    color="pink"
                    dot
                    offset-x="7"
                    offset-y="5"
                  >
                    <span v-bind="props">
                      <v-icon
                        :style="{
                          color: $vuetify.theme.dark
                            ? 'var(--v-theme-on-surface)'
                            : 'var(--v-theme-on-background)',
                        }"
                        :icon="'mdi-bell-outline'"
                      />
                    </span>
                  </v-badge>
                </template>

                <!-- No badge state -->
                <template v-else>
                  <span v-bind="props">
                    <v-icon
                      :style="{
                        color: $vuetify.theme.dark
                          ? 'var(--v-theme-on-surface)'
                          : 'var(--v-theme-on-background)',
                      }"
                      :icon="'mdi-bell-outline'"
                    />
                  </span>
                </template>
              </template>

              <span>{{ $t('navbar.tooltips.notifications') }}</span>
            </v-tooltip>
          </v-btn>

          <!-- Dashboard (admin area) -->
          <v-list-item
            class="dashboard-icon"
            icon
            v-for="item in dashboard"
            :key="item.icon"
            @click="sendComponent(item)"
          >
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-icon
                    :style="{
                      color: $vuetify.theme.dark
                        ? 'var(--v-theme-on-surface)'
                        : 'var(--v-theme-on-background)',
                    }"
                    :icon="'mdi-gauge'"
                  />
                </span>
              </template>
              <span>{{ item.titleKey ? $t(item.titleKey) : item.title }}</span>
            </v-tooltip>
          </v-list-item>

          <!-- Theme toggle (feature-flagged/tester or superAdmin) -->
          <v-btn
            icon
            @click="handleTheme"
            v-if="
              checkAuthorization(['superAdmin', 'tester']) ||
              (getFeatures && getFeatures.includes('theme'))
            "
          >
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-icon
                    :style="{
                      color: $vuetify.theme.dark
                        ? 'var(--v-theme-on-surface)'
                        : 'var(--v-theme-on-background)',
                    }"
                    icon="mdi-invert-colors"
                  />
                </span>
              </template>
              <span>{{ $t('navbar.tooltips.changeTheme') }}</span>
            </v-tooltip>
          </v-btn>

          <!-- User avatar -> opens profile dialog -->
          <v-list-item class="mx-3">
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-avatar>
                    <v-img
                      v-if="user.url"
                      @click="userProfile = true"
                      :src="user.url"
                      max-width="40"
                      width="40"
                      height="40"
                      cover
                    />
                    <v-icon v-else icon="mdi-account-circle" />
                  </v-avatar>
                </span>
              </template>
              <span>{{ $t('navbar.tooltips.editProfile') }}</span>
            </v-tooltip>
          </v-list-item>

          <!-- Misc top-right menu items (includes logout) -->
          <v-list-item
            icon
            v-for="item in menu"
            :key="item.icon"
            @click="sendComponent(item)"
            :class="{ 'logout-icon-item': item.component === 'Logout' }"
          >
            <v-tooltip location="bottom">
              <template #activator="{ props }">
                <span v-bind="props" :style="{ color: iconColor }">
                  <v-icon :icon="item.icon" />
                </span>
              </template>
              <span>{{ item.titleKey ? $t(item.titleKey) : item.title }}</span>
            </v-tooltip>
          </v-list-item>
        </v-toolbar-items>
      </v-app-bar>

      <!-- ===================== -->
      <!-- ADMIN LAYOUT WRAPPER -->
      <!-- ===================== -->
      <v-row class="admin-layout" no-gutters>
        <!-- LEFT COLUMN: Compact icon-only main menu -->
        <v-col cols="2" class="left-hand-menu">
          <v-navigation-drawer
            v-model="drawer"
            :key="drawerKey"
            :rail="mini"
            rail-width="66"
            width="66"
            :permanent="mdAndUp"
            :temporary="smAndDown"
            :scrim="false"
            class="left-hand-admin-menu custom-drawer-bg"
            :class="{
              'top-38': hasNotifications.length /* push down under alert */,
              'admin-menu-open':
                treeDrawer /* style when secondary drawer open */,
            }"
          >
            <!-- Icon list of authorized admin items -->
            <v-list>
              <v-list-item
                v-for="(item, index) in authorized"
                :key="index"
                @click="sendComponent(item)"
                :class="[
                  `icon-${item.icon.replace('mdi-', '')}`,
                  { 'is-active': isActive(item) },
                ]"
                class="d-flex flex-column align-center"
              >
                <!-- Tooltip for compact (rail) mode -->
                <v-tooltip v-if="mini" location="end">
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <v-icon :icon="item.icon" class="mb-1" />
                    </div>
                  </template>
                  <span>{{
                    item.titleKey ? $t(item.titleKey) : item.title
                  }}</span>
                </v-tooltip>

                <!-- Label under icon when in rail mode -->
                <div
                  v-if="mini"
                  class="text-center text-uppercase font-weight-bold"
                >
                  {{ item.titleKey ? $t(item.titleKey) : item.title }}
                </div>

                <!-- Full name when not in rail mode -->
                <div v-else class="text-center">
                  <span class="text-body-1">{{
                    item.titleKey ? $t(item.titleKey) : item.title
                  }}</span>
                </div>
              </v-list-item>
            </v-list>

            <!-- Toggle button to open/close the secondary tree-style drawer -->
            <div
              class="toggle-tab"
              v-if="checkAuthorization(['owner', 'superAdmin'])"
              @click="treeDrawer = !treeDrawer"
            >
              <v-icon
                :icon="treeDrawer ? 'mdi-chevron-left' : 'mdi-chevron-right'"
              />
            </div>
          </v-navigation-drawer>
        </v-col>

        <!-- MIDDLE COLUMN: Secondary tree-style drawer (grouped navigation) -->
        <v-col
          v-if="checkAuthorization(['owner', 'superAdmin'])"
          cols="4"
          class="tree-style-column"
        >
          <v-navigation-drawer
            v-model="treeDrawer"
            floating
            :scrim="false"
            class="tree-style-menu elevation-1 custom-second-drawer-bg"
            :style="{
              background: $vuetify.theme.themes[theme].secondaryNavBackground,
            }"
          >
            <!-- Group 1: Communication & Help -->
            <v-list density="compact" v-model:opened="openGroups">
              <v-list-group
                value="communicationList"
                fluid
                class="grouped-section custom-group-section"
                :color="$vuetify.theme.dark ? '#CECECE' : '#231815'"
              >
                <template #activator="{ props }">
                  <v-list-item v-bind="props" class="slim-list-item">
                    <template #prepend
                      ><v-icon>mdi-chevron-down</v-icon></template
                    >
                    <template #append />
                    <v-list-item-title>
                      {{ $t('navbar.admin.groups.communication') }}
                    </v-list-item-title>
                  </v-list-item>
                </template>

                <v-list-item
                  v-for="(item, index) in filteredCommunicationItems"
                  :key="`communication-${index}`"
                  @click="sendComponent(item)"
                >
                  <v-list-item-title>
                    {{ item.titleKey ? $t(item.titleKey) : item.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list-group>
            </v-list>

            <!-- Group 2: Notifications & Updates -->
            <v-list density="compact" v-model:opened="openGroups">
              <v-list-group
                value="notificationList"
                fluid
                class="grouped-section custom-group-section"
                :color="$vuetify.theme.dark ? '#CECECE' : '#231815'"
              >
                <template #activator="{ props }">
                  <v-list-item v-bind="props" class="slim-list-item">
                    <template #prepend
                      ><v-icon>mdi-chevron-down</v-icon></template
                    >
                    <template #append />
                    <v-list-item-title>
                      {{ $t('navbar.admin.groups.notifications') }}
                    </v-list-item-title>
                  </v-list-item>
                </template>

                <v-list-item
                  v-for="(item, index) in filteredNotificationItems"
                  :key="`notifications-${index}`"
                  @click="sendComponent(item)"
                >
                  <v-list-item-title>
                    {{ item.titleKey ? $t(item.titleKey) : item.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list-group>
            </v-list>

            <!-- Group 3: Administration & Management -->
            <v-list density="compact" v-model:opened="openGroups">
              <v-list-group
                value="adminList"
                fluid
                class="grouped-section custom-group-section"
                :color="$vuetify.theme.dark ? '#CECECE' : '#231815'"
              >
                <template #activator="{ props }">
                  <v-list-item v-bind="props" class="slim-list-item">
                    <template #prepend
                      ><v-icon>mdi-chevron-down</v-icon></template
                    >
                    <template #append />
                    <v-list-item-title>
                      {{ $t('navbar.admin.groups.administration') }}
                    </v-list-item-title>
                  </v-list-item>
                </template>

                <v-list-item
                  v-for="(item, index) in filteredAdminItems"
                  :key="`admin-${index}-${activeLocale}`"
                  @click="sendComponent(item)"
                >
                  <v-list-item-title>
                    {{ item.titleKey ? $t(item.titleKey) : item.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list-group>
            </v-list>
          </v-navigation-drawer>
        </v-col>
      </v-row>
    </nav>

    <!-- =============================== -->
    <!-- NON-ADMIN NAV WRAPPER (Public) -->
    <!-- =============================== -->
    <nav v-else>
      <!-- Hide app bar on PDF routes (viewer pages) -->
      <v-app-bar
        v-if="
          routeBaseName !== 'Pdf' &&
          routeBaseName !== 'SectionPdf' &&
          routeBaseName !== 'UnitPdf' &&
          routeBaseName !== 'CoursePdf'
        "
        flat
        color="transparent"
        class="px-4"
        :class="[
          { 'top-38': hasNotifications.length } /* push down under alert */,
          {
            'shifted-left': notificationDrawer && !smAndDown,
          } /* shift if drawer open on desktop */,
        ]"
      >
        <!-- Floating Courses Speed Dial (mobile-friendly button) -->
        <v-speed-dial
          v-if="
            user &&
            user.emailVerified &&
            user.active &&
            !user.archived &&
            isNotAdmin &&
            !checkAuthorization(['superAdmin', 'admin'])
          "
          fixed
          top
          left
        >
          <template #activator>
            <div
              class="speed-dial-container contextual-text-courses"
              :data-label="$t('navbar.menu.courses')"
            >
              <CoursesIcon
                :color="courseIcons"
                @click.stop="courseDrawer = !courseDrawer"
              />
            </div>
          </template>
        </v-speed-dial>

        <v-spacer></v-spacer>

        <!-- Brand text logo for non-admin pages (hidden on certain auth routes) -->
        <div
          v-if="
            routeBaseName !== 'Welcome' &&
            routeBaseName !== 'License' &&
            routeBaseName !== 'Login' &&
            routeBaseName !== 'Home'
          "
          class="text-uppercase mt-2"
        >
          <router-link :to="localePath('/')" class="brandlogo" exact>
            <v-icon :icon="'logo_icon'" />
          </router-link>
        </div>

        <v-spacer></v-spacer>

        <!-- Compact USER MENU (icon menu that expands) -->
        <div class="user-menu-container text-center">
          <!-- Toggle to open/close the menu panel; disabled if notes panel open -->
          <div
            class="mb-4 menu-icon-link"
            :data-label="$t('navbar.menu.label')"
            @click="
              !(notesOpen || highlightsOpen || commentsOpen)
                ? (visible = !visible)
                : null
            "
          >
            <v-icon slot="activator" :icon="'menu_icon'" />
          </div>

          <!-- Slide/fade menu content -->
          <Transition name="fade">
            <div
              class="menu-items-container"
              v-if="visible && !(notesOpen || highlightsOpen || commentsOpen)"
            >
              <!-- Profile (only if has cid or superAdmin, and profileComplete) -->
              <div class="menu-item">
                <v-tooltip
                  location="left"
                  v-if="
                    (cid || checkAuthorization(['superAdmin'])) &&
                    this.profileComplete
                  "
                >
                  <template #activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        class="profile-icon-link"
                        @click.stop="userProfile = true"
                        :icon="'profile'"
                      />
                    </span>
                  </template>
                  <span>{{ $t('navbar.tooltips.userProfile') }}</span>
                </v-tooltip>
              </div>

              <!-- Notifications (feature-flagged or tester role) -->
              <div class="menu-item">
                <v-tooltip
                  location="left"
                  v-if="
                    (cid && checkAuthorization(['superAdmin', 'tester'])) ||
                    (getFeatures && getFeatures.includes('notifications'))
                  "
                >
                  <template #activator="{ props }">
                    <!-- Badge if there are unread notifications -->
                    <template v-if="shouldShowNotificationBadge">
                      <v-badge
                        :content="unreadCount"
                        color="pink"
                        dot
                        location="left"
                        offset-y="20"
                      >
                        <span v-bind="props">
                          <v-icon
                            :color="$vuetify.theme.dark ? '#CECECE' : '#231815'"
                            @click.stop="showNotifications"
                            class="mt-2"
                            :icon="'notifications'"
                          />
                        </span>
                      </v-badge>
                    </template>

                    <!-- No badge state -->
                    <template v-else>
                      <span v-bind="props">
                        <v-icon
                          :color="$vuetify.theme.dark ? '#CECECE' : '#231815'"
                          @click.stop="showNotifications"
                          class="mt-2"
                          :icon="'notifications'"
                        />
                      </span>
                    </template>
                  </template>
                  <span>{{ $t('navbar.tooltips.notifications') }}</span>
                </v-tooltip>
              </div>

              <!-- FAQs (opens Support Chat) -->
              <div class="menu-item" v-if="supportChatProvider !== 'none'">
                <v-tooltip location="left">
                  <template #activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        @click.stop="openSupportChat()"
                        class="mt-2"
                        :icon="'userfaq'"
                      />
                    </span>
                  </template>
                  <span>{{ $t('navbar.tooltips.faqs') }}</span>
                </v-tooltip>
              </div>

              <!-- Privacy Policy -->
              <div class="menu-item">
                <v-tooltip location="left">
                  <template #activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        @click.stop="openPrivacyPolicyDialog"
                        class="mt-2"
                        :icon="'privacy'"
                      />
                    </span>
                  </template>
                  <span>{{ $t('navbar.tooltips.privacyPolicy') }}</span>
                </v-tooltip>
              </div>

              <!-- Theme toggle (feature-flagged or tester/superAdmin role) -->
              <div
                class="menu-item"
                v-if="
                  user &&
                  user.emailVerified &&
                  (checkAuthorization(['superAdmin', 'tester']) ||
                    (getFeatures && getFeatures.includes('theme'))) &&
                  this.$route.name !== 'Home'
                "
              >
                <v-tooltip location="left">
                  <template #activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        @click.stop="handleTheme"
                        class="_user-theme-icon mt-2"
                        icon="invert_colors"
                      />
                    </span>
                  </template>
                  <span>{{ $t('navbar.tooltips.changeTheme') }}</span>
                </v-tooltip>
              </div>

              <!-- Admin Dashboard shortcut (if role present, shows even in non-admin shell) -->
              <div class="menu-item">
                <v-tooltip
                  location="left"
                  v-if="checkAuthorization(['superAdmin', 'admin', 'leader'])"
                >
                  <template #activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        class="mt-2 admin-dashboard-link"
                        @click.stop="sendComponent({ component: 'Dashboard' })"
                        :icon="'admin'"
                      />
                    </span>
                  </template>
                  <span>{{ $t('navbar.tooltips.adminDashboard') }}</span>
                </v-tooltip>
              </div>

              <!-- Language -->
              <div class="menu-item" v-if="enabledLangs.length > 1">
                <v-menu location="left" offset="6">
                  <template #activator="{ props: menuProps }">
                    <v-tooltip location="left">
                      <template #activator="{ props: tipProps }">
                        <span v-bind="{ ...menuProps, ...tipProps }">
                          <v-icon class="mt-2" :icon="'language'" />
                        </span>
                      </template>
                      <span>{{ $t('navbar.lang.menuAria') }}</span>
                    </v-tooltip>
                  </template>

                  <v-list density="compact" class="tj-lang-menu">
                    <v-list-item
                      v-for="l in enabledLangs"
                      :key="l.code"
                      @click="onChangeLanguage(l.code)"
                    >
                      <v-list-item-title
                        class="d-flex align-center justify-space-between"
                      >
                        <span>{{ l.label }} ({{ l.code.toUpperCase() }})</span>
                        <v-icon
                          v-if="activeLocale === l.code.toLowerCase()"
                          size="16"
                          color="#A0C548"
                          >mdi-check</v-icon
                        >
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>

              <!-- Logout -->
              <div class="menu-item">
                <v-tooltip location="left">
                  <template #activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        @click.stop="confirmDialog = true"
                        class="mt-2"
                        :icon="'user_logout'"
                      />
                    </span>
                  </template>
                  <span>{{ $t('navbar.tooltips.logout') }}</span>
                </v-tooltip>
              </div>
            </div>
          </Transition>
        </div>
      </v-app-bar>
    </nav>

    <!-- ========================== -->
    <!-- COURSE NAVIGATION DRAWER  -->
    <!-- Visible for learners; placement adapts to viewport -->
    <!-- ========================== -->
    <v-navigation-drawer
      v-if="
        isNotAdmin &&
        !checkAuthorization(['admin', 'superAdmin']) &&
        !isPrintCourseRoute
      "
      v-model="courseDrawer"
      :style="courseDrawerStyle"
      :class="[
        { 'mobile-bottom-drawer': smAndDown } /* mobile: bottom sheet style */,
        {
          'top-38': hasNotifications.length && smAndUp,
        } /* desktop: push down under banner */,
      ]"
      :location="courseDrawerLocation"
      temporary
      :scrim="true"
      width="500"
      disable-resize-watcher
      id="course-drawer"
    >
      <CurrentCourseMenu />
    </v-navigation-drawer>

    <!-- ========================= -->
    <!-- NOTIFICATIONS RIGHT DRAWER -->
    <!-- ========================= -->
    <v-navigation-drawer
      v-show="true"
      v-model="notificationDrawer"
      :location="drawerLocation"
      :width="400"
      :temporary="smAndDown"
      :scrim="false"
      class="notification-drawer"
      :class="[
        'notification-drawer',
        { 'mobile-bottom-drawer': smAndDown } /* mobile: bottom sheet style */,
        {
          'top-38': hasNotifications.length && smAndUp,
        } /* desktop: push down under banner */,
      ]"
      :style="drawerStyle"
      disable-resize-watcher
      :color="isDarkTheme ? '#1e1e1e' : '#fff'"
    >
      <!-- Drawer header -->
      <v-toolbar
        :elevation="3"
        :color="isDarkTheme ? '#2c2c2c' : '#fff'"
        :class="isDarkTheme ? 'text-white' : 'text-black'"
        class="pr-4"
      >
        <v-btn
          icon
          :class="{ 'pl-4': smAndDown }"
          @click="notificationDrawer = false"
        >
          <v-icon :icon="'close'" size="small" />
        </v-btn>

        <v-toolbar-title class="text-body-1 pl-0 ml-0">{{
          $t('navbar.notifications.title')
        }}</v-toolbar-title>

        <v-spacer></v-spacer>

        <!-- Mark all notifications as read -->
        <v-btn
          variant="flat"
          size="small"
          rounded="0"
          color="#a5d867"
          class="overlay-hover font-weight-bold text-white"
          :class="{ 'mr-4': smAndDown }"
          @click="markAllRead"
        >
          {{ $t('navbar.notifications.markAllRead') }}
        </v-btn>
      </v-toolbar>

      <!-- Busy indicator while processing -->
      <v-progress-linear
        :active="processing"
        :indeterminate="processing"
        color="#592892"
      />

      <!-- Notifications list -->
      <v-list
        lines="two"
        activatable
        multiple
        v-model:selected="selected"
        class="font-weight-regular"
      >
        <template
          v-for="(item, index) in hasAllNotifications"
          :key="item.notification"
        >
          <v-list-item
            :disabled="processing"
            @click.prevent="manageNotification(item)"
            :value="item"
          >
            <div>
              <!-- Date/time -->
              <v-list-item-subtitle class="font-weight-regular">
                {{ new Date(item.created) | prettyDate }}
              </v-list-item-subtitle>

              <!-- Message (bold if unread) -->
              <v-list-item-title
                class="text--primary text-wrap"
                :class="{ 'font-weight-bold': !item.hasRead }"
              >
                <span v-html="getNotificationContent(item)"></span>
              </v-list-item-title>
            </div>

            <!-- Right-side action + star (filled if unread) -->
            <v-list-item-action>
              <span>{{ item.action }}</span>

              <v-icon
                v-if="item.hasRead"
                color="grey-lighten-1"
                :icon="'mdi-star-outline'"
              />
              <v-icon v-else color="#a5d867" :icon="'mdi-star'" />
            </v-list-item-action>
          </v-list-item>

          <!-- Divider between items -->
          <v-divider v-if="index < hasAllNotifications.length - 1" />
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- ================= -->
    <!-- CONFIRM LOGOUT   -->
    <!-- ================= -->
    <v-dialog v-model="confirmDialog" persistent max-width="400px">
      <v-card :color="isDarkTheme ? '#2d2d2d' : 'rgba(255,255,255,.95)'">
        <v-card-title class="text-h6 pt-3 pb-0 text-center">{{
          $t('navbar.dialogs.logout.title')
        }}</v-card-title>
        <v-card-text class="text-center text-body-2 pt-0 pb-5">
          {{ $t('navbar.dialogs.logout.body') }}
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn
            class="overlay-hover font-weight-bold btn-logout"
            @click="logout"
          >
            {{ $t('navbar.dialogs.logout.confirm') }}
          </v-btn>
          <v-btn
            class="overlay-hover btn-cancel"
            @click="confirmDialog = false"
          >
            {{ $t('navbar.dialogs.logout.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= -->
    <!-- FAQs DIALOG       -->
    <!-- ================= -->
    <v-dialog v-model="faqDialog" max-width="800px">
      <v-toolbar text color="#a5d867">
        <v-card-text class="font-weight-bold faq-toolbar-title">
          {{ $t('navbar.dialogs.faqs.title') }}
        </v-card-text>
        <v-spacer />
        <v-toolbar-items>
          <v-btn variant="text" @click="faqDialog = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <FaqsList />
    </v-dialog>

    <!-- ================= -->
    <!-- PROFILE DIALOG    -->
    <!-- e1 === 1 : View   -->
    <!-- e1 === 2 : Edit   -->
    <!-- ================= -->
    <v-dialog
      v-model="userProfile"
      persistent
      transition="scale-transition"
      max-width="420"
      @after-leave="resetProfileDialog"
    >
      <v-card class="py-6 px-8" min-height="465px">
        <!-- Profile View -->
        <template v-if="e1 === 1">
          <v-row justify="center" class="pt-8 pb-4">
            <v-avatar size="160">
              <v-img
                :src="user.url || require('@/assets/default-avatar.png')"
                cover
              />
            </v-avatar>
          </v-row>

          <v-card-text class="text-center text-h5 text-uppercase pb-2">
            {{ user.user_name }}
          </v-card-text>

          <v-card-text class="text-center pb-0">
            <span class="font-weight-bold">{{
              $t('navbar.dialogs.profile.email')
            }}</span>
            {{ user.email }}
          </v-card-text>

          <!-- Roles / Group labels -->
          <v-card-text class="text-center pt-0">
            <span v-if="isSuperAdmin()" class="font-weight-bold">
              {{ $t('navbar.dialogs.profile.superAdmin') }}
            </span>
            <span v-else-if="isHelpDesk()" class="font-weight-bold">
              {{ $t('navbar.dialogs.profile.helpDesk') }}
            </span>
            <span v-else-if="isChurchAdmin()" class="font-weight-bold">
              {{ $t('navbar.dialogs.profile.churchAdmin') }}
            </span>
            <span v-else class="font-weight-bold">
              {{ $t('navbar.dialogs.profile.group') }}
            </span>
            {{ assignedGroup() }}
            <span v-if="isLeaderOfGroup()" class="font-italic">
              {{ $t('navbar.dialogs.profile.leader') }}
            </span>
          </v-card-text>

          <!-- License info -->
          <v-card-text class="text-center py-0">
            <span class="font-weight-bold">{{
              $t('navbar.dialogs.profile.license')
            }}</span>
            {{
              $t('navbar.dialogs.profile.expires', {
                date: $helpers.getExpiryDate(user.activated),
              })
            }}
          </v-card-text>

          <v-card-actions class="justify-center pt-6">
            <v-btn
              variant="flat"
              color="#383935"
              class="text-white font-weight-bold text-uppercase rounded px-6 py-2"
              @click="userProfile = false"
            >
              {{ $t('common.close') }}
            </v-btn>
            <v-btn
              variant="flat"
              color="#A5D867"
              class="text-white font-weight-bold text-uppercase rounded px-6 py-2"
              @click="e1 = 2"
            >
              {{ $t('common.edit') }}
            </v-btn>
          </v-card-actions>
        </template>

        <!-- Profile Editor -->
        <template v-else-if="e1 === 2">
          <ProfileEditor :key="componentKey" :e1="e1" />
          <v-card-actions class="justify-center">
            <v-btn
              variant="flat"
              color="#383935"
              class="text-white font-weight-bold"
              @click="e1 = 1"
            >
              {{ $t('common.back') }}
            </v-btn>
            <v-btn
              variant="flat"
              color="rgba(56, 57, 53, 0.75)"
              class="text-white font-weight-bold"
              @click="userProfile = false"
            >
              {{ $t('common.close') }}
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <!-- ================= -->
    <!-- MEDIA MANAGER     -->
    <!-- ================= -->
    <v-dialog v-model="mediaDialog" width="800px" max-width="80%">
      <v-card :class="[isDarkTheme ? 'app-dark' : 'app-light']">
        <v-toolbar flat class="px-4 py-2" color="#592892">
          <v-toolbar-title class="text-white text-h6">
            {{ $t('navbar.dialogs.media.title') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn variant="text" class="text-white" @click="mediaDialog = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-toolbar>

        <Media :imgs="imgs" @update-imgs="updateImgs" />
      </v-card>
    </v-dialog>

    <!-- ================= -->
    <!-- CATEGORIES DIALOG -->
    <!-- ================= -->
    <v-dialog v-model="categoriesDialog" width="800px" max-width="80%">
      <v-card :class="[isDarkTheme ? 'app-dark' : 'app-light']">
        <v-toolbar flat class="px-4 py-2" color="#592892">
          <v-toolbar-title class="text-white text-h6">
            {{ $t('navbar.dialogs.categories.title') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            variant="text"
            class="text-white"
            @click="categoriesDialog = false"
          >
            {{ $t('common.close') }}
          </v-btn>
        </v-toolbar>

        <Categories :categorybackdoor="categorybackdoor" />
      </v-card>
    </v-dialog>

    <!-- ================= -->
    <!-- PRIVACY POLICY    -->
    <!-- ================= -->
    <v-dialog v-model="privacyPolicyDialog" max-width="800px">
      <v-card>
        <v-toolbar color="#592892" class="px-4">
          <v-toolbar-title>{{
            $t('navbar.dialogs.privacy.title')
          }}</v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            @click="privacyPolicyDialog = false"
            :aria-label="$t('common.close')"
          >
            <v-icon :icon="'mdi-close'" />
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <p v-html="privacyPolicyContent"></p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ================= -->
    <!-- IMAGE URL MODAL   -->
    <!-- Used by editor to insert image from Media Library -->
    <!-- ================= -->
    <div id="imageUrlDialog">
      <v-card
        :color="$vuetify.theme.dark ? '#2d2d2d' : 'rgba(255,255,255,.95)'"
        class="pa-4"
      >
        <v-card-title class="text-h6 pb-0 justify-center">
          {{ $t('navbar.imageUrl.title') }}
        </v-card-title>

        <v-card-text class="text-center">
          {{ $t('navbar.imageUrl.body') }}
        </v-card-text>

        <!-- Plain input (picked up by quill helper script) -->
        <div class="v-input__slot">
          <input
            class="pt-0"
            type="text"
            id="image-url-input"
            :placeholder="$t('navbar.imageUrl.placeholder')"
            autocomplete="off"
          />
        </div>

        <v-card-actions class="justify-center pb-4">
          <v-btn color="#383935" id="image-url-cancel">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn class="font-weight-bold" color="#a5d867" id="image-url-submit">
            {{ $t('navbar.imageUrl.insert') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>

<script>
  import firebase from 'firebase'
  import db from '@/firebase/init'
  import { mapState } from 'vuex'
  import { mapGetters } from 'vuex'
  import ProfileEditor from '@/components/utilities/ProfileEditor'
  import Media from '@/components/utilities/Media'
  import Categories from '@/components/utilities/Categories'
  import FaqsList from '@/components/utilities/FaqsList'
  import CurrentCourseMenu from '@/components/utilities/CurrentCourseMenu'
  import DisplayNotifications from '@/components/utilities/DisplayNotifications'
  import BrowserUpdateWarning from '@/components/utilities/BrowserUpdateWarning.vue'
  import ChurchLocaleBanner from '@/components/utilities/ChurchLocaleBanner.vue'
  import LogoIcon from '@/assets/icons/LogoIcon.vue'
  import { useDisplay } from 'vuetify'
  import emitter from '@/eventBus'
  import { getCurrentInstance, watch } from 'vue'
  import { currentTheme } from '@/plugins/vuetify'
  import CoursesIcon from '@/assets/icons/CoursesIcon.vue'
  import { useLanguages } from '@/helpers/useLanguages'
  import {
    getSavedSiteLocale,
    getDateFormatConfig,
    setSiteLocale,
  } from '@/helpers/siteLocale'
  import { prettyDate } from '@/helpers/stringUtils'
  import { i18n } from '@/plugins/i18n'
  import {
    initLiveHelperChat,
    openLiveHelperChat,
  } from '@/services/LiveHelperChatService'
  import { openZendesk } from '@/services/ZendeskService'
  import { DEFAULT_LIFE_ON_LIFE_KB_URL } from '@/constants/lifeOnLifeSupport'
  import {
    localeFullPath,
    currentLocale,
    localePath,
  } from '@/helpers/localePath'

  // i18n translations
  const { t } = i18n.global

  // Global validation rules (translatable)
  export const rules = {
    length: (len) => (v) =>
      (v || '').length >= len || t('validation.minLength', { len }),
    required: (v) => !!v || t('validation.required'),
  }

  export default {
    data() {
      return {
        stopAuthWatch: null,
        stopChurchesWatch: null,
        selected: null,
        marker: false,
        subGroupMarker: false,
        feedback: null,
        notification: null,
        allNotifications: [],
        hasNotifications: [],
        hasAllNotifications: [],
        roles: [],
        imgs: [],
        claims: '',
        fab: true,
        visible: true,
        stepper: false,
        e1: 1,
        confirmDialog: false,
        faqDialog: false,
        mediaDialog: false,
        categoriesDialog: false,
        privacyPolicyDialog: false,
        privacyPolicyContent: '',
        userProfile: false,
        componentKey: 0,
        drawer: this.mdAndUp,
        mini: true,
        drawerKey: 0,
        notificationDrawer: false,
        group: null,
        categorybackdoor: 0,
        notificationbackdoor: 0,
        hideOnScroll: true,
        screenWidth: null,
        hasResized: true,
        churches: [],
        allSections: [],
        allUnits: [],
        allGroups: [],
        processing: false,
        theNotesAreOpen: true,
        unitNotes: false,
        // active_el:0,
        churchsearch: '',
        treeDrawer: false,
        drawerLocationInternal: 'right',
        justSwitchedBreakpoint: false,
        courseDrawerLocationInternal: 'left',
        items: [
          {
            title: 'Churches',
            titleKey: 'navbar.menu.churches',
            icon: 'mdi-church',
            component: 'Churches',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'Courses',
            titleKey: 'navbar.menu.courses',
            icon: 'courses',
            component: 'Courses',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
          },
          {
            title: 'Sections',
            titleKey: 'navbar.menu.sections',
            icon: 'sections',
            component: 'Sections',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
          },
          {
            title: 'Units',
            titleKey: 'navbar.menu.units',
            icon: 'units',
            component: 'Units',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin'],
          },
          {
            title: 'Missions',
            titleKey: 'navbar.menu.missions',
            icon: 'missions',
            component: 'MissionsBuilder',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin'],
          },
          {
            title: 'M/S UNits',
            titleKey: 'navbar.menu.missionUnits',
            icon: 'mission_outline',
            component: 'Weeks',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin'],
          },
          {
            title: 'Groups',
            titleKey: 'navbar.menu.groups',
            icon: 'groups',
            component: 'Groups',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
          },
          {
            title: 'Users',
            titleKey: 'navbar.menu.users',
            icon: 'users',
            component: 'Users',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin'],
          },
          {
            title: 'Pending',
            titleKey: 'navbar.menu.pending',
            icon: 'users',
            component: 'PendingUsers',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin'],
          },
          {
            title: 'API',
            titleKey: 'navbar.menu.api',
            icon: 'api',
            component: 'ApiKey',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin'],
          },
          // You can uncomment later and add keys:
          // { title: 'FAQ', titleKey: 'navbar.menu.faqs', icon: '$faq', component: 'Faqs', claims: '', size: '', roles: ['superAdmin'] },
          // { title: 'Guide', titleKey: 'navbar.menu.guide', icon: '$guide', component: 'AppGuide', claims: '', size: '', roles: ['superAdmin'] },
        ],
        menu: [
          // {
          //   title: 'Profile',
          //   titleKey: 'navbar.menu.profile', // <-- you'll need to add this in JSON if you use it
          //   icon: 'home',
          //   component: "Profile",
          //   photoURL: this.$store.getters.getUser.photoURL
          // },
          // {
          //   title: 'Dashboard',
          //   titleKey: 'navbar.menu.dashboard',
          //   icon: 'dashboard',
          //   component: "Dashboard"
          // },
          // {
          //   title: 'Media',
          //   titleKey: 'navbar.menu.mediaLibrary',
          //   icon: '$media',
          //   component: 'Media',
          //   claims: '',
          //   size: '',
          //   roles: ['superAdmin']
          // },
          {
            title: 'Logout',
            titleKey: 'navbar.menu.logout',
            icon: 'logout',
            component: 'Logout',
            size: '',
          },
          // {
          //   title: 'Login',
          //   titleKey: 'navbar.menu.login', // <-- add to JSON if you want
          //   icon: 'dashboard',
          //   component: "Signin",
          //   size: ''
          // },
          // {
          //   title: 'Register',
          //   titleKey: 'navbar.menu.register', // <-- add to JSON if you want
          //   icon: 'home',
          //   component: "Register",
          //   size: ''
          // },
        ],
        category: [
          {
            title: 'Category Manager',
            titleKey: 'navbar.menu.categoryManager',
            icon: '$category',
            component: 'Category',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        media: [
          {
            title: 'Media Library',
            titleKey: 'navbar.menu.mediaLibrary',
            icon: '$media',
            component: 'Media',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        faqs: [
          {
            title: 'FAQ',
            titleKey: 'navbar.menu.faqs',
            icon: '$question_mark',
            component: 'Faqs',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        appGuide: [
          {
            title: 'APP GUIDE',
            titleKey: 'navbar.menu.guide',
            icon: '$guide',
            component: 'AppGuide',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        orientation: [
          {
            title: 'JOURNEY ORIENTATION',
            titleKey: 'navbar.menu.journeyOrientation',
            icon: '$guide',
            component: 'JourneyOrient',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        msgs: [
          {
            title: 'MESSAGES',
            titleKey: 'navbar.menu.messages',
            icon: '$guide',
            component: 'Messages',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        tutorials: [
          {
            title: 'TUTORIALS',
            titleKey: 'navbar.menu.tutorials',
            icon: '$guide',
            component: 'Tutorials',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        options: [
          {
            title: 'OPTIONS',
            titleKey: 'navbar.menu.options',
            icon: '$guide',
            component: 'Options',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        stripe: [
          {
            title: 'STRIPE',
            titleKey: 'navbar.menu.stripe',
            icon: '$guide',
            component: 'StripeKey',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        errors: [
          {
            title: 'LOGS',
            titleKey: 'navbar.menu.logs',
            icon: '$guide',
            component: 'ErrorsLog',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        notifications: [
          {
            title: 'NOTIFICATIONS',
            titleKey: 'navbar.menu.notifications',
            icon: '$guide',
            component: 'Notifications',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin'],
          },
        ],
        terms: [
          {
            title: 'TERMS',
            titleKey: 'navbar.menu.terms',
            icon: '$guide',
            component: 'ViewTerms',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin'],
          },
        ],
        newfeatures: [
          {
            title: 'NEW FEATURES',
            titleKey: 'navbar.menu.newFeatures',
            icon: '$guide',
            component: 'NewFeatures',
            claims: '',
            size: '',
            roles: ['owner'],
          },
        ],
        dashboard: [
          {
            title: 'Dashboard',
            titleKey: 'navbar.menu.dashboard',
            icon: 'dashboard',
            component: 'Dashboard',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
          },
        ],
        home: [
          {
            title: 'Home',
            titleKey: 'navbar.menu.home',
            icon: 'home',
            component: 'Home',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
          },
        ],
        footermenu: [
          {
            title: 'Guide',
            titleKey: 'navbar.menu.guide', // ⚠️ add this to en.json + pt.json
            icon: '$guide',
            component: 'AppGuide',
            claims: '',
            size: '',
            roles: 'admin',
          },
          {
            title: 'Courses',
            titleKey: 'navbar.menu.courses',
            icon: '$courses',
            component: 'Course',
            claims: '',
            size: '',
            roles: 'admin',
          },
          {
            title: 'Sections',
            titleKey: 'navbar.menu.sections',
            icon: '$sections',
            component: 'Sections',
            claims: '',
            size: '',
            roles: 'admin',
          },
          {
            title: 'Units',
            titleKey: 'navbar.menu.units',
            icon: '$units',
            component: 'Units',
            claims: '',
            size: '',
            roles: 'admin',
          },
        ],
        // Control state for each group independently
        openGroups: ['communicationList', 'notificationList', 'adminList'],

        communicationItems: [
          {
            title: 'APP GUIDE',
            titleKey: 'navbar.menu.appGuide',
            icon: '$guide',
            component: 'AppGuide',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'JOURNEY ORIENTATION',
            titleKey: 'navbar.menu.journeyOrientation',
            icon: '$guide',
            component: 'JourneyOrient',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'MESSAGES',
            titleKey: 'navbar.menu.messages',
            icon: '$guide',
            component: 'Messages',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'FAQ',
            titleKey: 'navbar.menu.faqs',
            icon: '$question_mark',
            linkType: 'supportChat',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'TUTORIALS',
            titleKey: 'navbar.menu.tutorials',
            icon: '$guide',
            component: 'Tutorials',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'TERMS',
            titleKey: 'navbar.menu.terms',
            icon: '$guide',
            component: 'TermsAdmin',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'PRIVACY POLICY',
            titleKey: 'navbar.menu.privacyPolicy',
            icon: '$guide',
            component: 'PrivacyPolicy',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        notificationItems: [
          {
            title: 'NOTIFICATIONS',
            titleKey: 'navbar.menu.notifications',
            icon: '$guide',
            component: 'Notifications',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'admin'],
          },
          {
            title: 'NEW FEATURES',
            titleKey: 'navbar.menu.newFeatures',
            icon: '$guide',
            component: 'NewFeatures',
            claims: '',
            size: '',
            roles: ['owner'],
          },
        ],
        adminItems: [
          // {
          //   title: 'DASHBOARD',
          //   titleKey: 'navbar.menu.dashboard',
          //   icon: 'dashboard',
          //   component: 'Dashboard',
          //   claims: '',
          //   size: '',
          //   roles: ['superAdmin', 'admin', 'leader'],
          // },
          {
            title: 'LOGS',
            titleKey: 'navbar.menu.logs',
            icon: '$guide',
            component: 'ErrorsLog',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'FEEDBACK',
            titleKey: 'navbar.menu.feedback',
            icon: '$guide',
            component: 'Feedback',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk', 'owner'],
          },
          {
            title: 'LANGUAGES',
            titleKey: 'adminLanguages.title',
            icon: '$guide',
            component: 'AdminLanguages',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'OPTIONS',
            titleKey: 'navbar.menu.options',
            icon: '$guide',
            component: 'Options',
            claims: '',
            size: '',
            roles: ['owner'],
          },
          {
            title: 'STRIPE',
            titleKey: 'navbar.menu.stripe',
            icon: '$guide',
            component: 'StripeKey',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'MEDIA LIBRARY',
            titleKey: 'navbar.menu.mediaLibrary',
            icon: '$media',
            component: 'Media',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
          {
            title: 'CATEGORY MANAGER',
            titleKey: 'navbar.menu.categoryManager',
            icon: '$category',
            component: 'Category',
            claims: '',
            size: '',
            roles: ['superAdmin', 'helpDesk'],
          },
        ],
        // siteLang: getSavedSiteLocale() || 'en', // Moved to computed property for reactivity
      }
    },
    watch: {
      churchid() {
        this.loadGroups()
        this.watchChurches()
      },
      getRoles() {
        this.watchChurches()
      },
      profileComplete(newValue) {
        // Do whatever makes sense now
        if (newValue) {
          this.init()
        }
      },
      smAndDown(val) {
        this.courseDrawerLocationInternal = val ? 'bottom' : 'left'
        this.courseDrawer = false // close drawer on breakpoint switch

        this.drawerLocationInternal = val ? 'bottom' : 'right'

        this.drawer = !val

        if (this.notificationDrawer) {
          this.notificationDrawer = false
        }

        // ✨ Temporarily disable animation during resize
        this.justSwitchedBreakpoint = true
        setTimeout(() => {
          this.justSwitchedBreakpoint = false
        }, 300) // Adjust to match your transition duration
      },
      notesOpen(v) {
        if (v) this.visible = false
      },
      highlightsOpen(v) {
        if (v) this.visible = false
      },
      commentsOpen(v) {
        if (v) this.visible = false
      },
      $route(to) {
        const base = (to.meta && to.meta.baseName) || to.name
        if (base === 'Unit') {
          this.visible = false
        }
        const p = to.path || ""
        if (
          p.startsWith("/sectionpdf") ||
          p.startsWith("/unitpdf") ||
          p.startsWith("/coursepdf") ||
          p.includes("/sectionpdf") ||
          p.includes("/unitpdf") ||
          p.includes("/coursepdf") ||
          p.startsWith("/pdf/")
        ) {
          this.courseDrawer = false
        }
      },
    },
    components: {
      ProfileEditor,
      Media,
      Categories,
      FaqsList,
      CurrentCourseMenu,
      DisplayNotifications,
      BrowserUpdateWarning,
      ChurchLocaleBanner,
      LogoIcon,
      CoursesIcon,
    },
    computed: {
      logoColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      isDarkTheme() {
        return currentTheme.value === 'dark'
      },
      drawerStyle() {
        const isMobile = this.smAndDown

        const style = isMobile
          ? {
              height: '45vh !important',
              maxHeight: '45vh !important',
              width: '100% !important',
              bottom: '0 !important',
              top: 'auto !important',
            }
          : {
              // Optional: width on desktop can stay default or set manually
              width: '400px',
              top: '0',
              bottom: 'auto',
            }

        // ✅ Only suppress transition if we just resized
        if (!this.notificationDrawer) {
          style.transform = isMobile ? 'translateY(100%)' : 'translateX(100%)'

          if (this.justSwitchedBreakpoint) {
            style.transition = 'none'
          }
        }

        return style
      },
      courseDrawerStyle() {
        return {
          top: '0px !important',
          height: '100% !important',
          zIndex: 2000, // ensure above backdrop
        }
      },
      drawerLocation() {
        return this.notificationDrawer
          ? this.drawerLocationInternal
          : this.smAndDown
          ? 'bottom'
          : 'right'
      },
      courseDrawerLocation() {
        return this.courseDrawerLocationInternal
      },
      unreadCount() {
        if (
          !this.user ||
          !this.user.emailVerified ||
          !Array.isArray(this.allNotifications)
        ) {
          return 0
        }
        return this.allNotifications.filter((n) => !n.hasRead).length
      },
      shouldShowNotificationBadge() {
        return this.unreadCount > 0
      },
      dateFormatConfig() {
        return getDateFormatConfig()
      },
      activeLocale() {
        return currentLocale()
      },
      siteLang() {
        // Get locale from current route, fallback to saved locale, then default to 'en'
        const routeLocale = this.$route.params?.locale
        if (routeLocale) {
          return routeLocale.toLowerCase()
        }
        const saved = getSavedSiteLocale()
        return saved || 'en'
      },
      showChurchSelect() {
        const rts = [
          'Weeks',
          'MissionsBuilder',
          'Units',
          'Sections',
          'Courses',
          'Groups',
          'Users',
          'ApiKey',
          'Dashboard',
          'Notifications',
        ]
        return rts.includes(this.routeBaseName)
      },
      validChurches() {
        const result = this.churches
          .filter((c) => {
            const isValid =
              c &&
              typeof c === 'object' &&
              !Array.isArray(c) &&
              typeof c.churchtitle === 'string' &&
              typeof c.cid === 'string' &&
              typeof c.churchadmin === 'string' &&
              typeof c.churchadminemail === 'string'

            if (!isValid) {
              console.warn('🚫 Excluding invalid church entry:', c)
            }

            return isValid
          })
          .map((c) => ({
            ...c,
            displayTitle: `${c.churchtitle}, ${c.cid}`,
          }))

        // console.log("🔎 Item sample:", result[0]);

        return result
      },
      showCategoryManager() {
        const rts = ['Sections', 'Units']
        return rts.includes(this.routeBaseName)
      },
      isNotAdmin() {
        return (
          this.$route.path === '/' ||
          this.routeBaseName === 'Home' ||
          this.routeBaseName === 'Login' ||
          this.routeBaseName === 'License' ||
          this.routeBaseName === 'Archived' ||
          this.routeBaseName === 'Welcome' ||
          this.routeBaseName === 'Unit' ||
          this.routeBaseName === 'Pdf' ||
          this.routeBaseName === 'SectionPdf' ||
          this.routeBaseName === 'UnitPdf' ||
          this.routeBaseName === 'CoursePdf'
        )
      },
      /** Full-page print views: never show the course menu drawer on top. */
      isPrintCourseRoute() {
        const p = this.$route.path || ""
        return (
          p.startsWith("/sectionpdf") ||
          p.startsWith("/unitpdf") ||
          p.startsWith("/coursepdf") ||
          p.includes("/sectionpdf") ||
          p.includes("/unitpdf") ||
          p.includes("/coursepdf") ||
          p.startsWith("/pdf/")
        )
      },
      churchid: {
        get() {
          return this.$store.getters['AppData/cid']
        },
        set(value) {
          this.$store.dispatch('AppData/setChurchId', value)
        },
      },
      shouldShowCourseDrawer() {
        const isOpen = this.$store.getters['AppData/courseDrawer'] === true

        // Hide for admins & superAdmins
        if (
          this.checkAuthorization &&
          this.checkAuthorization(['superAdmin', 'admin'])
        ) {
          return false
        }

        // If you have helper methods:
        if (this.isSuperAdmin && this.isSuperAdmin()) return false
        if (this.isChurchAdmin && this.isChurchAdmin()) return false

        return isOpen
      },
      courseDrawer: {
        get() {
          return this.$store.getters['AppData/courseDrawer']
        },
        set(value) {
          // Never allow toggling the course drawer in the admin shell
          if (!this.isNotAdmin) return

          // Block admins/superAdmins from using it anywhere
          if (
            this.checkAuthorization &&
            this.checkAuthorization(['superAdmin', 'admin'])
          )
            return

          this.$store.dispatch('AppData/courseDrawer', value)
        },
      },
      notesOpen: {
        get() {
          return this.$store.getters['AppData/notesOpen']
        },
        set(value) {
          this.$store.dispatch('AppData/notesOpen', value)
        },
      },
      highlightsOpen: {
        get() {
          return this.$store.getters['AppData/highlightsOpen']
        },
        set(value) {
          this.$store.dispatch('AppData/highlightsOpen', value)
        },
      },
      commentsOpen: {
        get() {
          return this.$store.getters['AppData/commentsOpen']
        },
        set(value) {
          this.$store.dispatch('AppData/commentsOpen', value)
        },
      },
      authorized() {
        const userRoles = this.$store.getters['UserData/getRoles']
        const hasRole = this.items.filter((val) =>
          userRoles.some((role) => val.roles.includes(role))
        )
        return hasRole
      },
      theme() {
        return this.$vuetify.theme.dark ? 'dark' : 'light'
      },
      filteredCommunicationItems() {
        if (!Array.isArray(this.communicationItems)) return []

        return this.communicationItems.filter((item) => {
          if (item.linkType === 'supportChat' && this.supportChatProvider === 'none') {
            return false
          }
          return this.checkAuthorization(item.roles)
        })
      },
      filteredNotificationItems() {
        return this.notificationItems.filter((item) =>
          this.checkAuthorization(item.roles)
        )
      },
      filteredAdminItems() {
        return this.adminItems.filter((item) =>
          this.checkAuthorization(item.roles)
        )
      },
      courseIcons() {
        return currentTheme.value === 'dark'
          ? 'rgb(165, 216, 103)'
          : 'rgb(165, 216, 103)'
      },
      iconColor() {
        return this.$vuetify.theme.dark
          ? 'var(--v-theme-on-surface)'
          : 'var(--v-theme-on-background)'
      },
      ...mapState('UserData', ['user', 'profileComplete']),
      ...mapGetters('AppData', [
        'getChurchById',
        'getCourse',
        'units',
        'cid',
        'getActiveInternalNotifications',
        'getFeatures',
        'getSupportChatConfig',
      ]),
      getRoles() {
        return this.$store.getters['UserData/getRoles'] || [] // Ensure it's always an array
      },
      routeBaseName() {
        // use the original name if provided by the router, else fall back to current name
        return (
          (this.$route.meta && this.$route.meta.baseName) ||
          this.$route.name ||
          ''
        )
      },
      removeNavOffset() {
        return ['License', 'Login', 'Welcome'].includes(this.routeBaseName)
      },
      supportChatEnabled() {
        const features = this.getFeatures || []
        return Array.isArray(features) && features.includes('support-chat')
      },
      supportChatProvider() {
        const provider = this.getSupportChatConfig?.provider
        if (!this.supportChatEnabled) return 'none'
        return ['none', 'lhc', 'zendesk', 'lifeOnLifeSupport'].includes(provider)
          ? provider
          : 'lhc'
      },
    },
    methods: {
      prettyDate(date) {
        return prettyDate(date)
      },
      getNotificationContent(item) {
        // Get the current locale (from URL param)
        const currentLang = this.$route.params.locale || 'en'

        // First, look for translated content in variants
        if (item.variants && item.variants.default) {
          const variants = item.variants.default

          // Try to get content for current language
          if (variants[currentLang]) {
            return variants[currentLang]
          }
        }

        // Second, if current language matches source language, use original content
        if (currentLang === item.sourceLocale) {
          return item.notification || ''
        }

        // Third, fallback to English variant if available
        if (
          item.variants &&
          item.variants.default &&
          item.variants.default.en
        ) {
          return item.variants.default.en
        }
        // Fallback to original notification content
        return item.notification || ''
      },
      isRoute(name) {
        return this.routeBaseName === name
      },
      isActive(item) {
        return this.routeBaseName === item.component
      },
      targetRouteName(name) {
        // Use locale-suffixed route names on prefixed routes
        return this.$route.params?.locale ? `${name}__loc` : name
      },
      handler() {
        this.feedback = null
        this.stepper = false
        this.userProfile = false
        this.e1 = 1
      },
      stepHandler() {
        this.componentKey += 1
        this.e1 = 2
      },
      handleTheme() {
        currentTheme.value = currentTheme.value === 'dark' ? 'light' : 'dark'
        localStorage.setItem('dark_theme', currentTheme.value === 'dark')
      },
      resetProfileDialog() {
        this.e1 = 1
      },
      filterData(item, queryText) {
        return (
          (item?.churchtitle?.toLowerCase() || '').includes(
            queryText.toLowerCase()
          ) ||
          (item?.churchadmin?.toLowerCase() || '').includes(
            queryText.toLowerCase()
          ) ||
          (item?.churchadminemail?.toLowerCase() || '').includes(
            queryText.toLowerCase()
          )
        )
      },
      showNotifications() {
        // console.log('showing notifications')
        this.notificationDrawer = !this.notificationDrawer

        // let the global events bus know Notifications is open
        emitter.emit('openNotifications')
      },
      async markAllRead() {
        // console.log(this.hasAllNotifications)
        this.processing = true
        // use one batch for everything
        var batch = db.batch()

        for (const doc of this.hasAllNotifications) {
          let hasNotificationRef = db
            .collection('notificationsread')
            .where('notificationid', '==', doc.id)
            .where('userid', '==', this.user.uid)
          let hasNotification = await hasNotificationRef.get()

          if (hasNotification.docs.length == 0) {
            let notificationRef = db.collection('notificationsread').doc()
            batch.set(notificationRef, {
              notificationid: doc.id,
              userid: this.user.uid,
              created: Date.now(),
            })
          }
        }

        // let everything finish
        await batch.commit()
        this.notificationbackdoor++
        // console.log('BATCHES ADDED ')
      },
      async manageNotification(notification) {
        this.processing = true
        // this.allNotifications = []

        let hasNotificationRef = db
          .collection('notificationsread')
          .where('notificationid', '==', notification.id)
          .where('userid', '==', this.user.uid)
        let hasNotification = await hasNotificationRef.get()
        if (hasNotification.docs.length == 0) {
          db.collection('notificationsread')
            .add({
              notificationid: notification.id,
              userid: this.user.uid,
              created: Date.now(),
            })
            .then(() => {
              this.notificationbackdoor++
            })
            .catch((err) => {
              console.error('Error marking notification as read:', err)
              // Don't show user error for notification read failures as it's not critical
            })
        } else {
          for (const doc of hasNotification.docs) {
            db.collection('notificationsread')
              .doc(doc.id)
              .delete()
              .then(() => {
                this.notificationbackdoor++
              })
          }
        }
      },
      checkNotifications(e) {
        this.hasNotifications = e
      },
      checkAllNotifications(e) {
        if (!this.user || !this.user.uid) return

        this.hasAllNotifications = e
        this.allNotifications = e
        this.processing = false
      },
      async openSupportChat() {
        if (!this.supportChatEnabled) return
        const supportConfig = this.getSupportChatConfig || {}
        const provider = this.supportChatProvider

        if (provider === 'zendesk') {
          await openZendesk(supportConfig.zendesk || {})
          return
        }
        if (provider === 'lifeOnLifeSupport') {
          // The "?" menu items open the knowledge base; the floating support
          // button (LifeOnLifeSupportLauncher) keeps opening the ticket form.
          const raw =
            supportConfig.lifeOnLifeSupport &&
            typeof supportConfig.lifeOnLifeSupport.kbUrl === 'string'
              ? supportConfig.lifeOnLifeSupport.kbUrl.trim()
              : ''
          const url = raw || DEFAULT_LIFE_ON_LIFE_KB_URL
          window.open(url, '_blank', 'noopener,noreferrer')
          return
        }
        if (provider === 'lhc') {
          // Live Helper Chat widget is injected globally; this is a best-effort opener.
          // Falls back to opening the support site if the widget doesn't expose an API.
          initLiveHelperChat(supportConfig.lhc || {})
          openLiveHelperChat(supportConfig.lhc || {})
        }
      },
      onChangeLanguage(newLocale) {
        const code = String(newLocale).toLowerCase()
        // Persist choice before navigation so unprefixed /courses is not redirected
        // back to /pt/... by the router guard reading stale siteLang from localStorage.
        setSiteLocale(code, { skipI18n: true })
        const next = localeFullPath(this.$route.fullPath, code)
        this.$router.push(next)
      },
      async fetchPrivacyPolicy() {
        try {
          // Get current locale from route or default to 'en'
          const currentLocale = this.$route.params?.locale || 'en'

          // Fetch privacy policy document (single document with variants structure)
          const privacyDoc = await db
            .collection('privacy_policy')
            .doc('default')
            .get()

          if (privacyDoc.exists) {
            const data = privacyDoc.data()

            // Try to get content for current locale from variants.default.{locale}
            let content = null
            if (
              currentLocale !== 'en' &&
              data.variants?.default?.[currentLocale]
            ) {
              content = data.variants.default[currentLocale]
            }

            // Fallback to top-level content (English) if locale-specific content is empty or doesn't exist
            if (!content || !content.trim()) {
              content = data.content || ''
            }

            this.privacyPolicyContent =
              content || 'No Privacy Policy available.'
          } else {
            this.privacyPolicyContent = 'No Privacy Policy available.'
          }
        } catch (error) {
          console.error('Error fetching privacy policy:', error)
          this.privacyPolicyContent = 'Error loading Privacy Policy.'
        }
      },

      openPrivacyPolicyDialog() {
        this.fetchPrivacyPolicy() // Fetch content before opening
        this.privacyPolicyDialog = true
      },
      onClickOutside() {
        this.fab = true
      },
      toggleTheme() {
        this.$vuetify.theme.dark = true
      },
      isAuthorized(arr1, arr2) {
        const found = arr1.some((r) => arr2.includes(r))
        return found
      },
      assignedGroup() {
        if (this.isSuperAdmin()) return ''

        const usersgroup = this.allGroups.filter((obj) => {
          return (
            obj.cid == this.cid &&
            (obj.members.includes(this.user.uid) ||
              obj.leader.includes(this.user.uid))
          )
        })

        if (usersgroup && usersgroup.length > 0) return usersgroup[0].name

        return 'unassigned'
      },
      isLeaderOfGroup() {
        const isLeader = this.allGroups.find((u) => u.leader == this.user.uid)

        if (isLeader) return true

        return false
      },
      isChurchAdmin() {
        const userRoles = this.$store.getters['UserData/getRoles']

        if (userRoles) {
          const isAdmin = userRoles.includes('admin')

          if (isAdmin) return true
        }

        return false
      },
      isSuperAdmin() {
        const userRoles = this.$store.getters['UserData/getRoles']

        if (userRoles) {
          const isSuperAdmin = userRoles.includes('superAdmin')

          if (isSuperAdmin) return true
        }

        return false
      },
      clearField() {
        document.getElementById('image-url-input').value = ''
        this.value = ''
        // console.log('called ', this.value)
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles'] || []
        if (!Array.isArray(permissions) || permissions.length === 0) return false

        const normalizedPermissions = permissions.includes('superAdmin')
          ? [...permissions, 'helpDesk']
          : permissions

        return normalizedPermissions.some((role) => userRoles.includes(role))
      },
      goback() {
        this.$router.go(-1)
      },
      logout() {
        // Close all dialogs first to prevent Vuetify scroll strategy issues
        this.confirmDialog = false
        this.faqDialog = false
        this.userProfile = false
        this.mediaDialog = false
        this.categoriesDialog = false
        this.privacyPolicyDialog = false
        this.notificationDrawer = false

        // Use nextTick to ensure all dialogs are fully closed before logout
        this.$nextTick(() => {
          this.$store
            .dispatch('UserData/userSignOut')
            .then(() => {
              // ✅ Reset Vuex modules
              this.$store.commit('UserData/RESET')
              this.$store.commit('AppData/RESET')

              // 🧹 Clear persisted Vuex localStorage for Vue 3 key
              try {
                localStorage.removeItem('journey_v3')
                console.log(
                  '✅ Persisted Vuex state (journey_v3) cleared on logout'
                )
              } catch (err) {
                console.warn('⚠️ Could not clear persisted Vuex state', err)
              }

              // Reset local component state
              this.roles = []
              this.visible = false

              // Ensure Firebase is fully signed out
              firebase.auth().signOut()

              // OPTIONAL: Reload to fully reset app state
              // location.reload()
            })
            .catch((error) => {
              console.error('Error during logout:', error)
              // Still reset state even if logout fails
              this.$store.commit('UserData/RESET')
              this.$store.commit('AppData/RESET')
            })
        })
      },
      sendComponent(item) {
        if (!item) {
          // Default to Dashboard if no item is provided
          this.$router.push({
            name: this.targetRouteName('Dashboard'),
            params: { isAdmin: this.roles, locale: this.$route.params?.locale },
          })
          return
        }

        if (item.linkType === 'supportChat') {
          this.openSupportChat()
          return
        }

        switch (item.component) {
          case 'Media':
            this.mediaManager()
            break

          case 'Category':
            this.categorybackdoor++
            this.categoriesDialog = true
            break

          case 'Logout':
            this.confirmDialog = true
            break

          default:
            this.$router.push({
              name: this.targetRouteName(item.component),
              params: {
                isAdmin: this.roles,
                locale: this.$route.params?.locale,
              },
            })
            break
        }
      },
      updateImgs(imgs) {
        this.imgs = imgs
      },
      mediaManager() {
        this.imgs = []

        var storage = firebase.storage()
        var storageRef = storage.ref()
        // Create a reference under which you want to list
        var listRef = storageRef.child('library')

        // Find all the prefixes and items.
        listRef
          .listAll()
          .then((res) => {
            // res.prefixes.forEach(function(folderRef) {
            //     // All the prefixes under listRef.
            //     // You may call listAll() recursively on them.
            //     console.log('0 ', folderRef)
            // });
            res.items.forEach((itemRef) => {
              // console.log(itemRef)
              // All the items under listRef.
              var starsRef = storageRef.child(itemRef.fullPath)
              starsRef
                .getDownloadURL()
                .then((url) => {
                  // Insert url into an <img> tag to "download"
                  var imgObject = {
                    id: itemRef.name,
                    url: url,
                  }
                  this.imgs.push(imgObject)
                })
                .catch(function (error) {
                  console.log('e ', error)
                })
            })
          })
          .catch(function (error) {
            console.log(error)
            // Uh-oh, an error occurred!
          })
        this.mediaDialog = true
      },
      customChurchFilter(value, queryText, item) {
        if (!queryText || !item?.raw) return true

        const query = queryText.toLowerCase()

        const haystack = [
          item.raw.churchtitle,
          item.raw.cid,
          item.raw.churchadmin,
          item.raw.churchadminemail,
        ]
          .filter(Boolean)
          .map((s) => s.toLowerCase())
          .join(' ')

        return haystack.includes(query)
      },
      getChurchTitle(cid) {
        // console.log("cid ", cid);
        const c = this.churches.find((ch) => ch.cid === cid)
        // console.log("c ", c);
        return c ? `${c.churchtitle}, ${c.cid}` : ''
      },
      setChurch(value) {
        // guard against DOM events
        const churchId = value?.target ? null : value
        this.$store.dispatch('AppData/setChurchId', churchId)
      },
      showAllChurches() {
        // console.log('show all called', event)
        this.$store.dispatch('AppData/setChurchId', null)
      },
      initFileUpload() {
        // document.getElementsByClassName('fileinput')[0].click()
      },
      inputFilter(newFile, oldFile, prevent) {
        // console.log('1: ', newFile, '2: ', this.fullPath)

        if (this.fullPath) this.deleteImage(this.fullPath)

        if (newFile && !oldFile) {
          if (!/\.(gif|jpg|jpeg|png|webp)$/i.test(newFile.name)) {
            return prevent()
          }
        }
        if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
          newFile.url = ''
          let URL = window.URL || window.webkitURL
          if (URL && URL.createObjectURL) {
            newFile.url = URL.createObjectURL(newFile.file)
          }
        }
        this.feedback = null
      },
      notesAreOpen() {
        this.notificationDrawer = false
      },
      applyDirection(isRtl) {
        // HTML dir (most important)
        document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr')

        // Vuetify 3 RTL flag (handle both shapes defensively)
        try {
          if (this.$vuetify?.locale && 'isRtl' in this.$vuetify.locale) {
            this.$vuetify.locale.isRtl = isRtl
          } else if ('rtl' in (this.$vuetify || {})) {
            // some builds expose $vuetify.rtl
            this.$vuetify.rtl = isRtl
          }
        } catch (e) {
          // non-fatal
          console.warn('RTL apply warning:', e)
        }
      },
      // Churches and groups need a signed-in user (firestore.rules), and NavBar
      // is mounted before login, so load them whenever the user changes.
      // Groups of the current church only: other churches' groups are staff-only
      // in firestore.rules. Reloaded when the church changes.
      loadGroups() {
        this.allGroups = []
        const cid = this.churchid
        if (!cid || !firebase.auth().currentUser) return
        db.collection('groups')
          .where('cid', '==', cid)
          .get()
          .then((snapshot) => {
            if (cid !== this.churchid) return
            this.allGroups = snapshot.docs.map((doc) => ({
              ...doc.data(),
              group_doc_id: doc.id,
            }))
          })
          .catch((error) => console.error('Error loading groups:', error))
      },
      watchSignedInData() {
        if (this.stopAuthWatch) return
        this.stopAuthWatch = firebase.auth().onAuthStateChanged((user) => {
          if (this.stopChurchesWatch) this.stopChurchesWatch()
          this.stopChurchesWatch = null
          this.allGroups = []
          if (!user) {
            this.churches = []
            return
          }

          this.loadGroups()
          this.watchChurches()
        })
      },
      // Staff read every church; everyone else only their own (firestore.rules).
      // Re-run when the user's roles or church change.
      watchChurches() {
        if (this.stopChurchesWatch) this.stopChurchesWatch()
        this.stopChurchesWatch = null
        if (!firebase.auth().currentUser) return
        const isStaff = this.getRoles.some((r) =>
          ['superAdmin', 'helpDesk', 'owner'].includes(r)
        )
        const cid = this.churchid
        if (!isStaff && !cid) {
          this.churches = []
          return
        }
        let churchesRef = db.collection('churches')
        if (!isStaff) churchesRef = churchesRef.where('cid', '==', cid)
        this.stopChurchesWatch = churchesRef.onSnapshot(
          (snapshot) => {
            const result = []
            snapshot.forEach((doc) => {
              const data = doc.data()

              // Sanity check: skip non-object entries
              if (
                data &&
                typeof data === 'object' &&
                !Array.isArray(data) &&
                typeof data.churchtitle === 'string' &&
                typeof data.cid === 'string' &&
                typeof data.churchadmin === 'string' &&
                typeof data.churchadminemail === 'string'
              ) {
                data.id = doc.id
                if (!data.archive) {
                  result.push(data) // ✅ Only valid full objects
                }
              } else {
                console.warn('❌ Skipping malformed church doc:', doc.id, data)
              }
            })

            this.churches = result
          },
          (error) => {
            console.error('Error listening to churches collection:', error)
            // Don't show user error for churches loading as it's not critical for navigation
          }
        )
      },
      async init() {
        // console.log('init called')

        if (!this.user || !this.user.emailVerified) {
          this.allNotifications = []
          this.hasAllNotifications = []
        }

        // Churches and groups need a signed-in user (firestore.rules)
        this.watchSignedInData()

        // console.log("✅ churches[0] after fetch:", this.churches?.[0]);
        // console.log("✅ typeof churches[0]:", typeof this.churches?.[0]);

        // get all units
        let unitsRef = db.collection('units')
        unitsRef.get().then((snapshot) => {
          snapshot.forEach((doc) => {
            let unit = doc.data()
            unit.id = doc.id
            this.allUnits.push(unit)
          })
        })

        // on init check if the user is logged in. if so, show the menu
        if (
          (this.user && this.user.emailVerified) ||
          this.checkAuthorization(['superAdmin', 'admin'])
        ) {
          this.visible = true
        } else {
          this.visible = false
        }

        // make sure the notifications resets
        this.notificationbackdoor++

        // register our global event to listen for when the notes is open
        emitter.on('openNote', this.notesAreOpen)

        // check for all features
        this.$store.dispatch('AppData/getActiveFeatures', {}, { root: true })
      },
      setupDrawerEventListeners() {
        const adminDrawer = document.querySelector('.left-hand-admin-menu')
        const treeDrawer = document.querySelector('.tree-style-menu')

        if (adminDrawer) {
          adminDrawer.addEventListener('click', this.stopPropagation)
        }
        if (treeDrawer) {
          treeDrawer.addEventListener('click', this.stopPropagation)
        }
      },
      cleanupDrawerEventListeners() {
        const adminDrawer = document.querySelector('.left-hand-admin-menu')
        const treeDrawer = document.querySelector('.tree-style-menu')

        if (adminDrawer) {
          adminDrawer.removeEventListener('click', this.stopPropagation)
        }
        if (treeDrawer) {
          treeDrawer.removeEventListener('click', this.stopPropagation)
        }
      },
      stopPropagation(e) {
        e.stopPropagation()
      },
    },
    created() {
      this.init()

      this.treeDrawer = this.mdAndUp

      // 🔁 apply saved locale on boot
      const saved = getSavedSiteLocale()
      if (saved) {
        // Set vue-i18n locale (ref-safe)
        const gl = i18n.global
        if (gl?.locale && 'value' in gl.locale) gl.locale.value = saved
        else gl.locale = saved

        // siteLang is now a computed property, no need to set it manually
      }

      // When enabledLangs arrives from Firestore, compute RTL and apply it once
      this.$watch(
        () => [this.enabledLangs, this.siteLang],
        ([langs, currentLang]) => {
          if (!Array.isArray(langs) || !currentLang) return
          const match = langs.find((l) => l.code === currentLang)
          const rtl = !!match?.rtl
          this.applyDirection(rtl)
        },
        { immediate: true }
      )

      // watching the user. if logged out close the menu
      this.$store.watch(
        () => {
          return this.user
        },
        (newValue) => {
          // console.log('newValue', newValue)

          // make sure the notifications resets
          this.notificationbackdoor++

          //something changed do something
          if (!newValue) {
            this.visible = false
          }
        },
        //Optional Deep if you need it
        {
          deep: true,
        }
      )
    },
    mounted() {
      this.setupDrawerEventListeners()

      this.$nextTick(() => {
        if (this.mdAndUp) {
          this.treeDrawer = true
        }
      })

      if (this.$route.name === 'Unit') {
        this.visible = false
      }
    },
    beforeUnmount() {
      if (this.stopChurchesWatch) this.stopChurchesWatch()
      if (this.stopAuthWatch) this.stopAuthWatch()
      emitter.off('openNote', this.notesAreOpen)
      this.cleanupDrawerEventListeners()
    },
    setup() {
      const { smAndDown, smAndUp, mdAndUp } = useDisplay()
      const instance = getCurrentInstance()

      // 🟢 languages from Firestore (enabled only)
      const { enabledLangs, defaultLang } = useLanguages()

      watch(
        smAndDown,
        (isMobile, wasMobile) => {
          const ctx = instance?.proxy
          if (!ctx) return

          if (ctx.notificationDrawer && isMobile !== wasMobile) {
            ctx.notificationDrawer = false
            console.log('✅ Closed notificationDrawer due to breakpoint change')
          }

          if (ctx.drawer && isMobile !== wasMobile) {
            ctx.drawer = false
          }
        },
        { flush: 'post' }
      )

      return {
        smAndDown,
        smAndUp,
        mdAndUp,
        enabledLangs,
        defaultLang,
        localePath,
      }
    },
  }
</script>

<style scoped>
  .scrollable-app-bar {
    overflow-x: auto; /* Enable horizontal scrolling */
    white-space: nowrap; /* Prevent wrapping */
    -ms-overflow-style: none; /* Hide scrollbar on Internet Explorer and Edge */
    scrollbar-width: none; /* Hide scrollbar on Firefox */
  }

  .scrollable-app-bar::-webkit-scrollbar {
    display: none; /* Hide scrollbar on Chrome, Safari, and Opera */
  }

  header.top-38 {
    top: 38px;
  }
  .v-toolbar__content .v-toolbar-title {
    margin-inline-start: 0;
  }
  /* Normalize top bar icons */
  .top-right-menu .v-icon {
    font-size: 24px;
    width: 24px;
    height: 24px;
    line-height: 24px;
  }
  .logout-icon-item .v-icon {
    font-size: 17px !important; /* or whatever size you need */
    width: 17px;
    height: 17px;
  }

  /* Normalize the surrounding buttons and list items */
  .top-right-menu .v-btn,
  .top-right-menu .v-list-item {
    min-width: unset;
    padding: 0 8px;
  }

  /* Avatar image consistency */
  .top-right-menu .v-avatar {
    height: 40px;
    width: 40px;
  }
  .shifted-left {
    width: calc(100% - 400px) !important;
    margin-right: 400px;
  }
  .top-right-menu .v-avatar .v-img {
    object-fit: cover;
  }
  :deep(.v-list-item--density-default.v-list-item--one-line) {
    min-height: 85px;
    justify-content: center;
    padding-inline: 4px;
  }
  .left-hand-admin-menu {
    text-align: center;
  }
  .v-list--dense .v-list-item .v-list-item__icon {
    width: auto;
    height: auto;
  }
  .v-list-item__icon {
    margin: 6px 0;
  }
  .v-list-item {
    font-size: 10px;
  }
  .v-list-item__action--stack {
    align-items: center;
  }
  .v-input--is-dirty :deep(.v-list-item__content + input) {
    display: none;
  }
  .church-select-dropdown :deep(.v-select__selections .v-list-item__content) {
    padding: 8px 0;
  }
  .church-select-dropdown :deep(.v-icon.mdi-close) {
    font-size: 16px;
  }
  :deep(.church-select-dropdown.v-overlay__content) {
    display: block !important;
  }

  :deep(.church-select-dropdown .v-select-list) {
    justify-content: flex-start !important;
  }

  :deep(.church-select-dropdown .v-list) {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  :deep(.church-select-dropdown .v-list-item) {
    flex: 0 0 48%;
  }
  :deep(.v-overlay__content) {
    transition: none !important;
  }

  .v-select-list
    :deep(.v-list-item__title, )
    .v-select-list
    :deep(.v-list-item__subtitle) {
    white-space: pre-wrap;
  }
  .menu-icon-link {
    position: relative;
  }
  .menu-icon-link:before,
  .contextual-text-menu:before {
    content: attr(data-label);
    margin-left: -37px;
    text-transform: uppercase;
    font-size: 10px;
    font-weight: 700;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
  .contextual-text-courses {
    line-height: 0;
    cursor: pointer;
    /* position: fixed;
    top: 16px;
    left: 16px; */
  }
  .contextual-text-courses:before {
    content: attr(data-label);
    margin-left: 47px;
    text-transform: uppercase;
    font-size: 10px;
    font-weight: 700;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
  .v-icon {
    cursor: pointer;
  }
  .v-icon:focus {
    outline: none;
  }
  .v-avatar img {
    object-fit: cover;
  }
  .v-avatar.v-list-item__avatar {
    cursor: pointer;
  }
  .user-title {
    font-size: 25px;
  }
  .avatar-upload .v-avatar label {
    width: 100%;
    height: 200px;
  }
  .avatar-upload .file-uploads label,
  .avatar-upload .v-avatar label {
    cursor: pointer;
  }
  .v-avatar img,
  .v-avatar svg,
  .v-avatar .v-icon,
  .v-avatar .v-image,
  .v-avatar .v-responsive__content {
    display: flex;
  }
  .file-uploads {
    display: inline-flex;
  }
  .category-btn-container {
    display: flex;
  }
  :deep(.icon-church) .v-icon,
  .v-icon.mdi-church.v-icon {
    background: #fff;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    color: #000;
    font-size: 20px;
  }
  :deep(.icon-church) .v-icon.v-theme--dark {
    background-color: rgb(165, 216, 103);
    color: #383935;
  }
  :deep(.icon-church) svg {
    width: 20px;
    height: 20px;
  }
  .dashboard-icon {
    padding: 0 12px;
  }
  .theme--dark .mdi-church {
    background: #a5d867;
    color: #383935;
  }
  #image-url-input {
    flex: 1 1 auto;
    line-height: 20px;
    max-width: 100%;
    min-width: 0px;
    width: 100%;
    color: rgba(0, 0, 0, 0.87);
    margin-bottom: 8px;
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  }
  #imageUrlDialog .v-input__slot:before {
    bottom: -1px;
    content: '';
    left: 0;
    position: absolute;
    transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    width: 100%;
    border-style: solid;
    border-width: thin 0 0 0;
    border-color: rgba(0, 0, 0, 0.47);
  }
  #imageUrlDialog .v-input__slot:hover:before {
    border-color: rgba(0, 0, 0, 0.87);
  }
  #image-url-input:focus {
    outline: none;
  }
  #image-url-input::-webkit-input-placeholder {
    /* Edge */
    color: rgba(0, 0, 0, 0.38);
  }
  #image-url-input:-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: rgba(0, 0, 0, 0.38);
  }
  #image-url-input::placeholder {
    color: rgba(0, 0, 0, 0.38);
  }
  .user-theme-icon {
    font-size: 22px;
    background: #383935;
    border-radius: 50%;
    width: 38px;
    height: 38px;
  }
  .current-year .v-list-item__icon.grn-bookmark {
    margin-right: 7px;
    margin-left: -14px;
    width: 7px;
    height: auto;
    min-width: 7px;
  }
  .current-course-units .v-list-item__content {
    padding: 6px 0;
  }
  .faq-toolbar-title {
    line-height: 1.3;
  }
  .user-menu-container {
    position: fixed;
    top: 16px;
    right: 16px;
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 1s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
  .notification-drawer :deep(.v-list-item__title p) {
    margin-bottom: 0;
  }
  .notification-drawer :deep(.v-list-item__action--stack) {
    align-self: center;
  }
  .notification-drawer.v-navigation-drawer--absolute,
  .notification-drawer.v-navigation-drawer--fixed {
    z-index: 1000000;
  }
  .notification-drawer.top-38,
  #course-drawer.top-38 {
    top: 38px !important;
  }
  #course-drawer.top-38 {
    padding-bottom: 38px;
  }

  .toggle-tab {
    position: absolute;
    right: -40px;
    bottom: 80px;
    background-color: rgb(207, 235, 174);
    color: white;
    border-radius: 0 4px 4px 0;
    padding: 8px;
    cursor: pointer;
  }
  .v-theme--dark .toggle-tab {
    background-color: #363636; /* dark mode - adminMenuBackground */
    color: #cecece;
  }

  .v-list--dense
    :deep(.custom-group-section .v-list-group__header__prepend-icon) {
    margin-right: 8px !important;
  }
  .v-list-item :deep(.v-list-item__prepend) {
    display: inherit;
  }
  .v-list-item :deep(.v-list-item__prepend) .v-icon {
    width: 24px;
    height: 24px;
    color: black;
  }
  .v-theme--dark .v-list-item :deep(.v-list-item__prepend) .v-icon {
    color: rgb(206, 206, 206);
  }
  .custom-group-section :deep(.v-list-group__items) {
    position: relative;
  }
  .custom-group-section :deep(.v-list-group__items > .v-list-item) {
    padding-left: 22px;
    margin-left: 40px;
  }
  .custom-group-section :deep(.v-list-group__items:before) {
    content: '';
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 27px;
    width: 2px;
    background-color: rgb(206 206 206);
    border-radius: 1px;
    height: 100%;
  }
  :deep(.custom-group-section .v-list-item:hover) {
    background-color: rgb(165 216 103 / 12%);
  }

  .v-list-item {
    cursor: pointer;
    transition: background-color 0.3s ease;
  }

  .v-list--density-compact .v-list-item .v-list-item-title {
    font-weight: 600;
    font-size: 0.75rem;
  }
  .v-theme--dark .v-list--density-compact .v-list-item .v-list-item-title {
    color: rgb(206, 206, 206);
  }
  .btn-logout {
    background-color: #a5d867;
    transition: background-color 0.3s;
  }

  .btn-cancel {
    background-color: #383935;
    transition: background-color 0.3s;
  }

  @media only screen and (min-width: 981px) {
    .tree-style-column {
      z-index: 1;
    }
  }
  @media only screen and (max-width: 767px) {
    .notification-drawer header {
      position: fixed;
      width: 100%;
      z-index: 1;
    }
    .notification-drawer .v-list {
      top: 56px;
      position: relative;
    }
  }
</style>
